/**
 * Tracks — the «Треки» tab: every track of the channel, «Исполнители», «Riddim», and the
 * lists they open.
 *
 * Task 05. `spec.md` stories 9–19. The tab has three sections behind one switch, and any
 * artist, riddim or tag opens its own list on top of them with a way back:
 *
 * ```
 * Все треки ─┐                     ┌─ карточка трека ─┬─ исполнитель ─┐
 * Исполнители ┼─ строка ─▶ список ──┤                  ├─ riddim ──────┼─▶ список
 * Riddim ─────┘                     └──────────────────┴─ тег ─────────┘
 * ```
 *
 * ## Paging follows the scroll
 *
 * `components/TrackListView.tsx`: the next page is asked for as the scroll nears the end. Task 11
 * moved the list there, for «Моё» to show its lists the same way.
 *
 * ## Places are remembered
 *
 * Opening an artist from the middle of «Все треки» and coming back lands on the same row.
 * The scroll position of every view is kept for the life of the tab, keyed like the lists;
 * a view opened for the first time starts at the top.
 */

import type { JSX } from 'react';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ScrollPortContext } from '../app/scroll-port';
import { ScreenHead } from '../components/ScreenHead';
import { TrackCard } from '../components/TrackCard';
import { ListState, TrackListView, playIn } from '../components/TrackListView';
import { ArtistIndex } from '../components/ArtistIndex';
import { RiddimTiles } from '../components/RiddimTiles';
import { player } from '../engine/player';
import type { TrackItem, TrackQuery } from '../lib/api';
import { trackCount, versionCount } from '../lib/format';
import { listKey, queryOf, useLibrary } from '../store/library';
import type { Collection, LibrarySection, SectionList } from '../store/library';
import '../styles/tracks.css';

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ──────────────────────────────────────────────────────────────────────────── */

export const TRACKS_TITLE = 'Треки';
export const BACK_LABEL = '← Назад';
export const LOADING_TEXT = 'Загружаю каталог…';
export const EMPTY_TRACKS_TEXT = 'Треки появятся здесь, как только их опубликуют в канале.';
export const EMPTY_COLLECTION_TEXT = 'Здесь пока нет треков.';
export const EMPTY_ARTISTS_TEXT = 'Исполнители появятся вместе с первыми треками.';
export const EMPTY_RIDDIMS_TEXT = 'Здесь появятся риддимы, на которых записаны треки канала.';
/** Task 69: the two buttons of an artist's or a riddim's page. */
export const PLAY_ALL_LABEL = 'Слушать всё';
export const SHUFFLE_ALL_LABEL = 'Вперемешку';

/** The switch, in the order the specification names the sections. */
export const SECTIONS: readonly { readonly id: LibrarySection; readonly label: string }[] =
  Object.freeze([
    { id: 'tracks', label: 'Все треки' },
    { id: 'artists', label: 'Исполнители' },
    { id: 'riddims', label: 'Riddim' },
  ]);

/** What the line under a collection's name calls it. */
const KIND_WORDS: Readonly<Record<Collection['kind'], string>> = {
  artist: 'Исполнитель',
  riddim: 'Riddim',
  tag: 'Тег',
};

/* ────────────────────────────────────────────────────────────────────────────
 * Behaviour constants
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * A list older than this is fetched again when the tab is entered.
 *
 * `spec.md` story 14 wants a new post in the app within a minute of it appearing in the
 * channel. The indexer gets it into the database; this is the half that gets it onto a
 * screen that was already open.
 */
export const REFRESH_AFTER_MS = 60_000;

/* ────────────────────────────────────────────────────────────────────────────
 * The card's «В очередь»
 * ──────────────────────────────────────────────────────────────────────────── */

/** Task 08: appends the track to the queue; `false` when the queue is full. */
function enqueue(track: TrackItem): boolean {
  return player().enqueue(track);
}

/* ────────────────────────────────────────────────────────────────────────────
 * A section list: artists or riddims
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * A section's whole place — «Исполнители» or «Riddim»: asked for on entry, «Загружаю…», a failure
 * with «Ещё раз», the empty sentence, or the section itself (task 69: by letters, as tiles).
 */
function SectionView<T>({
  section,
  emptyText,
  onLoad,
  children,
}: {
  readonly section: SectionList<T>;
  readonly emptyText: string;
  readonly onLoad: () => Promise<void>;
  readonly children: (items: readonly T[]) => JSX.Element;
}): JSX.Element {
  useEffect(() => {
    void onLoad();
  }, [onLoad]);

  if (section.status === 'idle' || section.status === 'loading') {
    return <ListState text={LOADING_TEXT} />;
  }
  if (section.status === 'failed') {
    return (
      <ListState
        text={section.failure?.message ?? LOADING_TEXT}
        onRetry={() => {
          void onLoad();
        }}
      />
    );
  }
  if (section.items.length === 0) return <ListState text={emptyText} />;
  return children(section.items);
}

/* ────────────────────────────────────────────────────────────────────────────
 * The screen
 * ──────────────────────────────────────────────────────────────────────────── */

/** The query of «Все треки». One object for the life of the app — see `TrackListView`. */
const ALL_TRACKS: TrackQuery = Object.freeze({ kind: 'all' });

/**
 * Task 69: the line under an opened list's name — «Исполнитель · 6 треков», «Riddim · 3 версии ·
 * 1985», «Тег · 12 треков». A riddim's year is its earliest version's among the rows loaded, and
 * none when no row has one; a count not known yet is left out rather than guessed.
 */
function collectionMeta(
  collection: Collection,
  total: number | undefined,
  items: readonly TrackItem[],
): string {
  const parts = [KIND_WORDS[collection.kind]];
  if (total !== undefined) {
    parts.push(collection.kind === 'riddim' ? versionCount(total) : trackCount(total));
  }
  if (collection.kind === 'riddim') {
    const years = items.flatMap((track) => (track.year === null ? [] : [track.year]));
    if (years.length > 0) parts.push(String(Math.min(...years)));
  }
  return parts.join(' · ');
}

/**
 * Task 69: «Вперемешку» — the same tracks in another order. Fisher–Yates; an order that came out
 * as it went in is turned by one, so the button never seems to have done nothing.
 */
export function shuffled<T>(list: readonly T[], random: () => number = Math.random): T[] {
  const out = [...list];
  for (let index = out.length - 1; index > 0; index -= 1) {
    const other = Math.floor(random() * (index + 1));
    const kept = out[index] as T;
    out[index] = out[other] as T;
    out[other] = kept;
  }
  if (out.length > 1 && out.every((item, index) => item === list[index]))
    out.push(out.shift() as T);
  return out;
}

function PlayGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="collectionIcon">
      <path d="M7 4.5v15l13-7.5z" fill="currentColor" />
    </svg>
  );
}

function ShuffleGlyph(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="collectionIcon"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 7h3c4 0 6 10 10 10h5M17 14l4 3-4 3M3 17h3c1.6 0 2.8-1.6 3.9-3.6M14 8.6C14.8 7.6 15.7 7 17 7h4M17 4l4 3-4 3" />
    </svg>
  );
}

/** The name a collection is shown under. A tag keeps its `#`, the way the channel writes it. */
function collectionTitle(collection: Collection): string {
  return collection.kind === 'tag' ? `#${collection.name}` : collection.name;
}

export function TracksScreen(): JSX.Element {
  const port = useContext(ScrollPortContext);
  const section = useLibrary((s) => s.section);
  const collection = useLibrary((s) => s.collection);
  const showSection = useLibrary((s) => s.showSection);
  const openCollection = useLibrary((s) => s.openCollection);
  const closeCollection = useLibrary((s) => s.closeCollection);
  const artists = useLibrary((s) => s.artists);
  const riddims = useLibrary((s) => s.riddims);
  const loadArtists = useLibrary((s) => s.loadArtists);
  const loadRiddims = useLibrary((s) => s.loadRiddims);

  const [card, setCard] = useState<TrackItem | undefined>(undefined);

  // The store keeps the same `collection` object until another one is opened, so this is
  // stable for as long as the list it names is on screen.
  const collectionQuery = useMemo(
    () => (collection === undefined ? undefined : queryOf(collection)),
    [collection],
  );
  const total = useLibrary((s) =>
    collectionQuery === undefined ? undefined : s.lists[listKey(collectionQuery)]?.total,
  );
  const collectionItems = useLibrary((s) =>
    collectionQuery === undefined ? undefined : s.lists[listKey(collectionQuery)]?.items,
  );
  // Task 66: the count beside the title is the whole catalogue's, whichever section is open.
  const allTotal = useLibrary((s) => s.lists[listKey(ALL_TRACKS)]?.total);

  /* ── Where each view was scrolled to ─────────────────────────────────────── */

  const viewKey = collectionQuery === undefined ? section : listKey(collectionQuery);
  const positions = useRef(new Map<string, number>());
  const remember = useCallback((): void => {
    if (port !== null) positions.current.set(viewKey, port.scrollTop);
  }, [port, viewKey]);

  useLayoutEffect(() => {
    if (port !== null) port.scrollTop = positions.current.get(viewKey) ?? 0;
  }, [port, viewKey]);

  /* ── Navigation ──────────────────────────────────────────────────────────── */

  const goToSection = useCallback(
    (next: LibrarySection): void => {
      remember();
      showSection(next);
    },
    [remember, showSection],
  );

  const goToCollection = useCallback(
    (next: Collection): void => {
      remember();
      setCard(undefined);
      // A list opened afresh starts at the top, even if it was open before from elsewhere.
      positions.current.delete(listKey(queryOf(next)));
      openCollection(next);
    },
    [remember, openCollection],
  );

  const goBack = useCallback((): void => {
    remember();
    closeCollection();
  }, [remember, closeCollection]);

  const closeCard = useCallback((): void => {
    setCard(undefined);
  }, []);

  const playFromCard = useCallback(
    (track: TrackItem): void => {
      playIn(listKey(collectionQuery ?? ALL_TRACKS), track);
    },
    [collectionQuery],
  );

  /* ── The body ────────────────────────────────────────────────────────────── */

  let body: JSX.Element;
  if (collection !== undefined && collectionQuery !== undefined) {
    body = (
      <TrackListView
        key={viewKey}
        query={collectionQuery}
        label={collectionTitle(collection)}
        emptyText={EMPTY_COLLECTION_TEXT}
        loadingText={LOADING_TEXT}
        maxAgeMs={REFRESH_AFTER_MS}
        onOpenTrack={setCard}
      />
    );
  } else if (section === 'artists') {
    body = (
      <SectionView section={artists} emptyText={EMPTY_ARTISTS_TEXT} onLoad={loadArtists}>
        {(items) => <ArtistIndex artists={items} label="Исполнители" onOpen={goToCollection} />}
      </SectionView>
    );
  } else if (section === 'riddims') {
    body = (
      <SectionView section={riddims} emptyText={EMPTY_RIDDIMS_TEXT} onLoad={loadRiddims}>
        {(items) => <RiddimTiles riddims={items} label="Riddim" onOpen={goToCollection} />}
      </SectionView>
    );
  } else {
    body = (
      <TrackListView
        key="all"
        query={ALL_TRACKS}
        label="Все треки"
        emptyText={EMPTY_TRACKS_TEXT}
        loadingText={LOADING_TEXT}
        maxAgeMs={REFRESH_AFTER_MS}
        onOpenTrack={setCard}
      />
    );
  }

  return (
    <div className="tracks">
      <header className="tracksHead">
        {collection === undefined ? (
          <>
            <ScreenHead
              title={TRACKS_TITLE}
              count={allTotal === undefined ? undefined : trackCount(allTotal)}
            />
            <div className="segments" role="group" aria-label="Разделы каталога">
              {SECTIONS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="segment"
                  aria-pressed={item.id === section}
                  onClick={() => {
                    goToSection(item.id);
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <button type="button" className="back" onClick={goBack}>
              {BACK_LABEL}
            </button>
            <ScreenHead
              title={collectionTitle(collection)}
              meta={collectionMeta(collection, total, collectionItems ?? [])}
            />
          </>
        )}
      </header>

      {collection !== undefined &&
        collection.kind !== 'tag' &&
        collectionItems !== undefined &&
        collectionItems.length > 0 && (
          <div className="collectionActions">
            <button
              type="button"
              className="button collectionPlay"
              onClick={() => {
                const first = collectionItems[0];
                if (first !== undefined) playIn(listKey(collectionQuery ?? ALL_TRACKS), first);
              }}
            >
              <PlayGlyph />
              {PLAY_ALL_LABEL}
            </button>
            <button
              type="button"
              className="button buttonSecondary collectionShuffle"
              onClick={() => {
                const order = shuffled(collectionItems);
                const first = order[0];
                if (first !== undefined) player().playFrom(order, first, null);
              }}
            >
              <ShuffleGlyph />
              {SHUFFLE_ALL_LABEL}
            </button>
          </div>
        )}

      {body}

      {card !== undefined && (
        <TrackCard
          track={card}
          onClose={closeCard}
          onOpenCollection={goToCollection}
          onPlay={playFromCard}
          onEnqueue={enqueue}
        />
      )}
    </div>
  );
}
